/**
 * GeoTranslator — Sprint 22.7.34 (Module 11 Geo-to-Portfolio Translator).
 *
 * Surfaces /api/geo-translator: 24h of high/critical geopolitical signals
 * clustered by region x type, translated to portfolio/watchlist exposure via
 * the deterministic text-keyed impact resolver. Exposure mapping only - no
 * invented direction calls. Raw signal feed lives at /geo-intel.
 * Closes the dead /geo sidebar link (Tier 1 dead-link sweep).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe2, Clock, AlertTriangle } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

const SEV_COLOR = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#6b7280' };
const MUTED = '#6b7280';

function fmtAge(iso) {
  if (!iso) return '-';
  const norm = iso.includes('T') ? iso : iso.replace(' ', 'T');
  const d = new Date(norm.endsWith('Z') || norm.includes('+') ? norm : norm + 'Z');
  if (isNaN(d.getTime())) return '-';
  const ms = Date.now() - d.getTime();
  if (ms < 3_600_000) return `${Math.max(1, Math.floor(ms / 60_000))}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function SevBadge({ sev }) {
  const color = SEV_COLOR[sev] || MUTED;
  return (
    <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded" style={{ color, backgroundColor: `${color}1a` }}>
      {sev}
    </span>
  );
}

function ClusterCard({ c, relevant }) {
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${relevant ? 'border-korinth-accent/40 bg-korinth-bg/40' : 'border-korinth-border/30 bg-korinth-bg/30'}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-white uppercase">{c.region}</span>
          <span className="text-[11px] font-mono text-korinth-muted">{String(c.signal_type).replace(/_/g, ' ')}</span>
          <SevBadge sev={c.severity} />
        </div>
        <span className="text-[9.5px] font-mono text-korinth-muted">{c.signal_count} signals · {fmtAge(c.latest_at)} · {(c.sources || []).join(', ')}</span>
      </div>

      {(c.summary_lines || []).slice(0, 2).map((s, i) => (
        <p key={i} className="text-[11px] text-korinth-text leading-snug">{s}</p>
      ))}

      {relevant && c.portfolio_touch?.length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase text-korinth-muted mb-1">
            <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1 text-amber-400" />Touches your book
          </div>
          <div className="flex flex-wrap gap-1.5">
            {c.portfolio_touch.map((p, i) => (
              <span key={i} title={p.reason} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-amber-400/40 bg-amber-400/10 cursor-help">
                <span className="text-white font-bold">{p.ticker}</span>
                {p.weight_pct != null && <span className="text-korinth-muted"> {p.weight_pct}%</span>}
                <span className="text-korinth-muted"> · {p.matched_by}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {relevant && c.watchlist_impact?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[9.5px] font-mono text-korinth-muted uppercase">watchlist:</span>
          {c.watchlist_impact.map((w, i) => (
            <span key={i} title={w.reason} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-korinth-border/40 cursor-help">
              {w.ticker} <span className="text-korinth-muted">({w.tier})</span>
            </span>
          ))}
        </div>
      )}

      {(c.sector_impacts?.length > 0 || c.theme_impacts?.length > 0) && (
        <div className="text-[9.5px] font-mono text-korinth-muted">
          {(c.sector_impacts || []).map(s => s.sector).join(', ')}
          {c.sector_impacts?.length > 0 && c.theme_impacts?.length > 0 ? ' · ' : ''}
          {(c.theme_impacts || []).map(t => t.theme).join(', ')}
        </div>
      )}
    </div>
  );
}

export default function GeoTranslator() {
  const { setPage } = usePage();
  useEffect(() => { setPage('geo'); }, []);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch('/api/geo-translator');
      const d = await r.json();
      if (!r.ok) { setErr(d.error || `HTTP ${r.status}`); return; }
      setData(d);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
  }, [load]);

  const relevant = data?.portfolio_relevant || [];
  const background = data?.background_clusters || [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-korinth-accent/10 border border-korinth-accent/30 flex items-center justify-center text-korinth-accent">
            <Globe2 className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Geo-to-Portfolio Translator</h1>
            <p className="text-[10px] font-mono text-korinth-muted">24h geo signal clusters translated to YOUR exposure · deterministic, no direction calls</p>
          </div>
        </div>
        {data?.as_of && (
          <div className="flex items-center gap-1.5 text-[10px] font-mono text-korinth-muted">
            <Clock className="w-3 h-3" /> {new Date(data.as_of).toISOString().slice(0, 16).replace('T', ' ')} UTC
          </div>
        )}
      </div>

      {loading && !data && <div className="text-[11px] font-mono text-korinth-muted animate-pulse">Clustering and translating geo signals…</div>}
      {err && <div className="text-[11px] font-mono text-red-400">Error: {err} <button onClick={load} className="ml-2 underline">retry</button></div>}

      {data && (
        <>
          <ModuleContainer
            title="Portfolio-relevant clusters"
            subtitle={`clusters whose signals touch held positions or watchlist · ${relevant.length} of ${relevant.length + background.length} active clusters`}
          >
            {relevant.length === 0 ? (
              <p className="text-[11px] font-mono text-korinth-muted">No active geo cluster currently touches your book or watchlist. Background clusters below.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {relevant.map((c) => <ClusterCard key={c.key} c={c} relevant />)}
              </div>
            )}
          </ModuleContainer>

          {background.length > 0 && (
            <ModuleContainer title="Background clusters" subtitle="active, but no detected exposure overlap">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {background.map((c) => <ClusterCard key={c.key} c={c} relevant={false} />)}
              </div>
            </ModuleContainer>
          )}

          <p className="text-[10px] font-mono text-korinth-muted italic border-t border-korinth-border pt-2">
            {data.note} Raw signal feed and map tools live on <Link to="/geo-intel" className="text-korinth-accent underline">Geo Intel</Link>.
          </p>
        </>
      )}
    </div>
  );
}
